// Education document logic

import { normalizeFieldsFromSchema } from "../shared/fieldNormalization";
import {
  mapSubjectsToTableRows,
  resolveSubjectRows,
  subjectsFromTableRows,
} from "../shared/tableFieldHelpers";
import { isSupportedUploadFile } from "../shared/uploadMime";

const required = (value) =>
  value && String(value).trim() ? null : "This field is required.";

const schema = {
  type: "education_document",
  title: "Education Document",
  groups: [
    { id: "details", label: "Details" },
    { id: "results", label: "Results" },
    { id: "dates", label: "Dates" },
  ],
  fields: [
    {
      key: "student_name",
      label: "Student Name",
      group: "details",
      inputType: "text",
      required: true,
      validate: required,
    },
    {
      key: "father_name",
      label: "Father Name",
      group: "details",
      inputType: "text",
    },
    {
      key: "qualification",
      label: "Qualification",
      group: "details",
      inputType: "text",
    },
    {
      key: "institute",
      label: "Institute / University",
      group: "details",
      inputType: "text",
    },
    { key: "board", label: "Board", group: "details", inputType: "text" },
    { key: "province", label: "Province", group: "details", inputType: "text" },
    { key: "country", label: "Country", group: "details", inputType: "text" },
    {
      key: "examination_type",
      label: "Examination Type",
      group: "details",
      inputType: "text",
    },
    {
      key: "roll_number",
      label: "Roll Number",
      group: "results",
      inputType: "text",
    },
    { key: "cgpa", label: "CGPA", group: "results", inputType: "text" },
    {
      key: "percentage",
      label: "Percentage",
      group: "results",
      inputType: "text",
    },
    { key: "grade", label: "Grade", group: "results", inputType: "text" },
    {
      key: "marks_obtained",
      label: "Marks Obtained",
      group: "results",
      inputType: "text",
    },
    {
      key: "total_marks",
      label: "Total Marks",
      group: "results",
      inputType: "text",
    },
    {
      key: "course_start_date",
      label: "Course Start Date",
      group: "dates",
      inputType: "date",
    },
    {
      key: "course_end_date",
      label: "Course End Date",
      group: "dates",
      inputType: "date",
    },
    {
      key: "date_time_of_issuance",
      label: "Date of Issuance",
      group: "dates",
      inputType: "date",
    },
  ],
  table: {
    title: "Detail of Marks Obtained",
    columns: ["Subject", "Marks Obtained", "Total Marks"],
  },
};

const enrichExtractedJson = (extracted) => ({ ...(extracted || {}) });

const getLlmExtractionFields = () => {
  const keys = schema.fields.map((field) => field.key);
  keys.push("subjects");
  return keys;
};

const validateFieldValue = (key, value) => {
  const def = schema.fields.find((field) => field.key === key);
  if (!def || typeof def.validate !== "function") return null;
  return def.validate(value);
};

const normalizeExtractedJson = (extracted, rawText = "") => {
  const enriched = enrichExtractedJson(extracted, rawText);
  return normalizeFieldsFromSchema(schema, enriched, rawText);
};

const buildTableFromExtracted = (data) => {
  const subjectRows = resolveSubjectRows(data || {});
  if (!subjectRows?.length) return null;

  return {
    title: schema.table?.title || "Detail of Marks Obtained",
    columns: schema.table?.columns || [
      "Subject",
      "Marks Obtained",
      "Total Marks",
    ],
    rows: mapSubjectsToTableRows(subjectRows),
  };
};

const appendTableToExtractedJson = (obj, tableData) => {
  const next = { ...obj };
  if (tableData?.rows?.length) {
    next.subjects = subjectsFromTableRows(tableData.rows);
  }
  return next;
};

export const educationDocumentType = {
  key: "education_document",
  schema,
  getLlmExtractionFields,
  enrichExtractedJson,
  normalizeExtractedJson,
  validateFieldValue,
  isSupportedFile: isSupportedUploadFile,
  buildTableFromExtracted,
  appendTableToExtractedJson,
};
